/**
 * Workflow merge HTML, pure: the one format of a workflow `text` value, read and written without a DOM so the API, the runtime and
 * the editor share it. Text is the HTML WorkBuddy's shared Froala merge editors write: every inserted field is
 * `<span class="fr-deletable wb-merge-tag" contenteditable="false" data-wb-merge="<name>">[Label]</span>`.
 * Two families of names share that span:
 * - Workflow tags, by root: `steps.<stepId>[.<path>]`, `item[.<path>]`, `vars.<name>`, `settings.<key>[.<path>]`,
 *   `flags.<flag>`, `trigger.<path>`, `workflow.name`, `run.startedAt` and `now`. The run resolves them (`renderWorkflowMergeHtml`),
 *   and a workflow tag may carry display options as `data-wb-format`, `data-wb-decimals`, `data-wb-if-empty` and `data-wb-join`.
 * - Record tags: every other name, exactly as the record's merge sources name it (`job.jobNumber`, `jobCustomer.name`); the server's
 *   merge renderer (`MergeUtility.mergeInner`) resolves them against the run's record, as it does for a merge template. Only a field
 *   that declares `recordTags` (`WorkflowElementFieldBase`) may hold them.
 * Invariant: no merge source may be named like a workflow root (`WORKFLOW_MERGE_ROOTS`); one that was would be read as a
 * workflow tag and render empty.
 */
import { WorkflowValueSource, type WorkflowMergeTag, type WorkflowMergeTagLabel, type WorkflowTokenFormat, type WorkflowValue, type WorkflowValueFormatOptions } from './workflow.definition';

/** The `data-wb-merge` roots a workflow resolves itself; every other name is a record tag. */
export const WORKFLOW_MERGE_ROOTS: readonly string[] = ['steps', 'item', 'vars', 'settings', 'flags', 'workflow', 'run', 'trigger', 'now'];

// A merge span as Froala writes it: single or double quoted, attributes in any order, plain text inside.
const MERGE_SPAN = /<span\b([^>]*?)\sdata-wb-merge\s*=\s*(["'])(.*?)\2([^>]*)>[\s\S]*?<\/span>/gi;
const OPTION_ATTRIBUTE = /\sdata-wb-(format|decimals|if-empty|join)\s*=\s*(["'])(.*?)\2/gi;

const ENTITIES: Record<string, string> = {
    amp: '&',
    lt: '<',
    gt: '>',
    quot: '"',
    apos: "'",
    nbsp: ' ',
};

/** Every merge tag of `html`, in reading order. Called by the validator, the evaluator and the editor. */
export function workflowMergeTags(html: string): WorkflowMergeTag[] {

    return [...html.matchAll(MERGE_SPAN)].map(match => {

        const name = decodeEntities(match[3]).trim();
        const tag: WorkflowMergeTag = {
            name,
            value: workflowMergeTagValue(name),
            start: match.index ?? 0,
            end: (match.index ?? 0) + match[0].length,
        };
        return { ...tag, ...optionsOf(`${match[1]} ${match[4]}`) };
    });
}

/** What the workflow tag `name` reads; null for a record tag, or for a name under a workflow root that does not parse. */
function workflowMergeTagValue(name: string): WorkflowValue | null {

    if (name === 'now') {
        return { source: WorkflowValueSource.Now };
    }
    if (name === 'workflow.name' || name === 'run.startedAt') {
        return { source: WorkflowValueSource.Trigger, path: name };
    }
    const [root, ...rest] = name.split('.');
    const path = rest.join('.');
    switch (root) {
        case 'trigger':
            return path ? { source: WorkflowValueSource.Trigger, path } : null;
        case 'steps':
            return rest[0] ? { source: WorkflowValueSource.Step, stepId: rest[0], path: rest.slice(1).join('.') || undefined } : null;
        case 'item':
            return { source: WorkflowValueSource.Item, path: path || undefined };
        case 'settings':
            return rest[0] ? { source: WorkflowValueSource.Setting, key: rest[0], path: rest.slice(1).join('.') || undefined } : null;
        case 'flags':
            return path ? { source: WorkflowValueSource.Flag, flag: path } : null;
        case 'vars':
            return path ? { source: WorkflowValueSource.Variable, name: path } : null;
        default:
            return null;
    }
}

/** The `data-wb-merge` name that reads `value`; null for a value no tag can hold (a literal, a list, text). */
export function workflowMergeTagName(value: WorkflowValue): string | null {

    switch (value.source) {
        case WorkflowValueSource.Now:
            return 'now';
        case WorkflowValueSource.Trigger:
            return value.path === 'workflow.name' || value.path === 'run.startedAt' || value.path === 'now'
                ? value.path
                : `trigger.${value.path}`;
        case WorkflowValueSource.Step:
            return `steps.${value.stepId}${value.path ? `.${value.path}` : ''}`;
        case WorkflowValueSource.Item:
            return `item${value.path ? `.${value.path}` : ''}`;
        case WorkflowValueSource.Setting:
            return `settings.${value.key}${value.path ? `.${value.path}` : ''}`;
        case WorkflowValueSource.Flag:
            return `flags.${value.flag}`;
        case WorkflowValueSource.Variable:
            return `vars.${value.name}`;
        default:
            return null;
    }
}

/**
 * The span the `#` picker inserts for `name`: `[label]` titled `label from source`, with `options` as `data-wb-*` attributes. A tag
 * without a label reads as `{{name}}`.
 */
export function workflowMergeTagHtml(name: string, label: WorkflowMergeTagLabel | undefined, options: WorkflowValueFormatOptions = {}): string {

    const attributes = [
        `class="fr-deletable wb-merge-tag"`,
        `contenteditable="false"`,
        `data-wb-merge="${escapeMergeText(name)}"`,
        ...(label?.source ? [`title="${escapeMergeText(`${label.label} from ${label.source}`)}"`] : []),
        ...optionAttributes(options),
    ];
    const text = label
        ? `[${label.label}]`
        : `{{${name}}}`;
    return `<span ${attributes.join(' ')}>${escapeMergeText(text)}</span>`;
}

/** `options` as the `data-wb-*` attributes a merge span carries, unset options left out. */
export function optionAttributes(options: WorkflowValueFormatOptions): string[] {

    const attributes: string[] = [];
    if (options.format !== undefined) {
        attributes.push(`data-wb-format="${escapeMergeText(options.format)}"`);
    }
    if (options.decimals !== undefined) {
        attributes.push(`data-wb-decimals="${options.decimals}"`);
    }
    if (options.ifEmpty) {
        attributes.push(`data-wb-if-empty="${escapeMergeText(options.ifEmpty)}"`);
    }
    if (options.join) {
        attributes.push(`data-wb-join="${escapeMergeText(options.join)}"`);
    }
    return attributes;
}

/**
 * `html` as plain text: a line break or block end as a new line, tags dropped, entities decoded, a merge span as its own text.
 * For what reads text only: an SMS, an email subject, a stop reason, a node summary.
 */
export function workflowMergeHtmlText(html: string): string {

    const text = html
        .replace(/<br\s*\/?>/gi, '\n')
        .replace(/<\/(p|div|li|h[1-6])>/gi, '\n')
        .replace(/<[^>]+>/g, '');
    return decodeEntities(text).replace(/\n+$/, '');
}

/**
 * `text` escaped for HTML. Braces are escaped too: the server renders record tags with Mustache over the whole text, so a resolved
 * value holding `{{…}}` would otherwise be rendered as a template.
 */
export function escapeMergeText(text: string): string {

    return text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;')
        .replace(/\{/g, '&#123;')
        .replace(/\}/g, '&#125;');
}

/** The format options a span's attributes carry; a decimals count that is not a number reads as NaN, which the validator refuses. */
function optionsOf(attributes: string): WorkflowValueFormatOptions {

    const options: WorkflowValueFormatOptions = {};
    for (const [, name, , raw] of attributes.matchAll(OPTION_ATTRIBUTE)) {
        const text = decodeEntities(raw);
        switch (name) {
            case 'format':
                options.format = text as WorkflowTokenFormat;
                break;
            case 'decimals':
                options.decimals = Number(text);
                break;
            case 'if-empty':
                options.ifEmpty = text;
                break;
            case 'join':
                options.join = text;
                break;
        }
    }
    return options;
}

function decodeEntities(text: string): string {

    return text.replace(/&(#x[0-9a-f]+|#\d+|[a-z]+);/gi, (entity, code: string) => {

        if (code[0] === '#') {
            const point = code[1] === 'x' || code[1] === 'X'
                ? parseInt(code.slice(2), 16)
                : parseInt(code.slice(1), 10);
            return Number.isFinite(point) ? String.fromCodePoint(point) : entity;
        }
        return ENTITIES[code.toLowerCase()] ?? entity;
    });
}
